import type { components as StrapiComponents } from '@core/types/clients/strapi'
import type { UseSeoMetaInput } from '@unhead/vue'
import { useRequestURL, useRoute, useStrapiMedia, useCoreConfig } from '#imports'

// Not reactive due to issues with memory leaks
export const useStrapiSeo = (seoData?: StrapiComponents['schemas']['SharedSeoComponent']) => {
    const route = useRoute()
    const { locale } = useCoreConfig()

    function getHead() {
        if (!seoData) return {}

        const script = []
        if (seoData.structuredData) {
            script.push({
                type: 'application/ld-json',
                children: JSON.stringify(seoData.structuredData),
            })
        }

        const link = []

        if (seoData.canonicalURL) {
            link.push({
                rel: 'canonical',
                href: `${seoData.canonicalURL}`,
            })
        }
        else if (Object.keys(route.query).length) {
            link.push({
                rel: 'canonical',
                href: route.path,
            })
        }

        return {
            script,
            link,
        }
    }

    function getSeoMeta(): UseSeoMetaInput {
        const url = useRequestURL()
        const { getStrapiMediaUrl } = useStrapiMedia()

        const defaults = {
            ogType: 'article' as const,
            ogLocale: locale.replace('-', '_'),
            ogUrl: `${url?.origin}${url?.pathname}`,
            ogImageUrl: `${url?.origin}/cdn-cgi/image/fit=scale-down,format=webp,quality=64,width=1200,height=630/media/logo_opengraph.jpg`,
            ogImageWidth: 1200,
            ogImageHeight: 630
        }

        if (!seoData) return defaults

        const og = seoData.metaSocial?.find((x: any) => x.socialNetwork === 'Facebook')
        const twitter = seoData.metaSocial?.find((x: any) => x.socialNetwork === 'Twitter')

        return {
            title: seoData?.metaTitle as string,
            description: seoData?.metaDescription,
            ogTitle: og?.title || seoData?.metaTitle,
            ogDescription: og?.description || seoData?.metaDescription,
            twitterTitle: twitter?.title || seoData?.metaTitle,
            twitterDescription: twitter?.description || seoData?.metaDescription,
            keywords: seoData?.keywords,
            robots: seoData?.metaRobots,

            ogType: defaults.ogType,
            ogLocale: defaults.ogLocale,
            ogUrl: defaults.ogUrl,
            ogImageUrl: getStrapiMediaUrl(seoData?.metaImage?.url) || defaults.ogImageUrl,
            ogImageWidth: seoData?.metaImage?.width || defaults.ogImageWidth,
            ogImageHeight: seoData?.metaImage?.height || defaults.ogImageHeight,
        }
    }

    return {
        getHead,
        getSeoMeta,
    }
}
